import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api';

// Constants

const CHALLENGE_NEW_PASSWORD = 'NEW_PASSWORD_REQUIRED';
const EXCEPTION_PASSWORD_RESET = 'PasswordResetRequiredException';
const EXCEPTION_USER_NOT_FOUND = 'UserNotFoundException';
const STORAGE_GR_AUTH_SESSION = 'gr.auth.session';
const MESSAGE_PASSWORD_RECOVERY_CODE = 'A password reset code has been sent to your email.';
const MESSAGE_ADMINISTRATOR_APPROVAL = 'Please wait until an administrator approves your account.';
const MESSAGE_PASSWORD_UPDATED = 'Your password has been updated successfully!';
const MESSAGE_EMAIL_VERIFIED = 'Your email address has been verified!';
const MESSAGE_USER_NOT_FOUND = 'User does not exist.';
const SEVERITY_ERROR = 'error';
const SEVERITY_INFO = 'info';
const SEVERITY_SUCCESS = 'success';

// Thunks

export const signIn = createAsyncThunk(
  'auth/signIn',
  async ({ username, password }, { rejectWithValue }) => {
    sessionStorage.clear();
    localStorage.clear();

    try {
      const data = await api.signIn(username, password);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const signOut = createAsyncThunk(
  'auth/signOut',
  async (payload, { rejectWithValue }) => {
    try {
      const data = await api.signOut();
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const completeNewPassword = createAsyncThunk(
  'auth/completeNewPassword',
  async (newPassword, { getState, rejectWithValue }) => {
    try {
      const data = await api.completeNewPassword(getState().auth.currentUser, newPassword);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (username, { rejectWithValue }) => {
    try {
      const data = await api.forgotPassword(username);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const forgotPasswordSubmit = createAsyncThunk(
  'auth/forgotPasswordSubmit',
  async ({ username, recoveryCode, newPassword }, { rejectWithValue }) => {
    try {
      const data = await api.forgotPasswordSubmit(username, recoveryCode, newPassword);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const verifyUserAttribute = createAsyncThunk(
  'auth/verifyUserAttribute',
  async ({ attribute }, { getState, rejectWithValue }) => {
    try {
      const data = await api.verifyUserAttribute(getState().auth.currentUser, attribute);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const verifyUserAttributeSubmit = createAsyncThunk(
  'auth/verifyUserAttributeSubmit',
  async ({ attribute, code }, { getState, rejectWithValue }) => {
    try {
      const data = await api.verifyUserAttributeSubmit(getState().auth.currentUser, attribute, code);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const signUp = createAsyncThunk(
  'auth/signUp',
  async ({
    username,
    password,
    email,
    firstName,
    lastName,
  }, { rejectWithValue }) => {
    try {
      const data = await api.signUp(username, password, email, firstName, lastName);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const setCurrentUser = createAsyncThunk(
  'auth/setCurrentUser',
  async (payload, { rejectWithValue }) => {
    try {
      const data = await api.getCurrentUserInfo();
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const setCurrentSession = createAsyncThunk(
  'auth/setCurrentSession',
  async (payload, { rejectWithValue }) => {
    try {
      const data = await api.getCurrentSession();
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Slice

export const authSlice = createSlice({
  name: 'auth',
  initialState: {
    currentUser: null,
    currentSession:
      (localStorage.getItem(STORAGE_GR_AUTH_SESSION)
        && localStorage.getItem(STORAGE_GR_AUTH_SESSION) !== 'No current user'
        && JSON.parse(localStorage.getItem(STORAGE_GR_AUTH_SESSION)))
      || (sessionStorage.getItem(STORAGE_GR_AUTH_SESSION)
        && sessionStorage.getItem(STORAGE_GR_AUTH_SESSION) !== 'No current user'
        && JSON.parse(sessionStorage.getItem(STORAGE_GR_AUTH_SESSION)))
      || null,
    isNewPasswordRequired: false,
    isForgotPassword: false,
    isUserRegistration: false,
    isRecoveryCodeSent: false,
    isPasswordReset: false,
    isUnverifiedEmail: false,
    isVerificationCodeSent: false,
    isPending: false,
    remember: false,
    message: '',
    severity: 'info',
  },
  reducers: {
    setIsForgotPassword: (state, action) => {
      state.isForgotPassword = action.payload;
    },
    setIsUserRegistration: (state, action) => {
      state.isUserRegistration = action.payload;
    },
    setIsPasswordReset: (state, action) => {
      state.isPasswordReset = action.payload;
    },
    setRememberStatus: (state, action) => {
      state.remember = action.payload;
    },
    setMessage: (state, action) => {
      const { message, severity } = action.payload;

      state.message = message;
      state.severity = severity;
    },
    clearMessage: (state) => {
      state.message = '';
    },
  },
  extraReducers: {
    [signIn.pending]: (state) => {
      state.isPending = true;
    },
    [signIn.fulfilled]: (state, action) => {
      state.currentUser = action.payload;
      state.isPending = false;

      if (state.currentUser.challengeName === CHALLENGE_NEW_PASSWORD) {
        state.isNewPasswordRequired = true;
      } else if (!state.currentUser.attributes.email_verified) {
        state.isUnverifiedEmail = true;
      } else {
        state.currentSession = state.currentUser.getSignInUserSession();
        state.isNewPasswordRequired = false;

        if (state.remember) {
          localStorage.setItem(STORAGE_GR_AUTH_SESSION, JSON.stringify(state.currentSession));
        } else {
          sessionStorage.setItem(STORAGE_GR_AUTH_SESSION, JSON.stringify(state.currentSession));
        }
      }
    },
    [signIn.rejected]: (state, action) => {
      state.isPending = false;
      state.message = action.payload && action.payload.message;
      state.severity = SEVERITY_ERROR;

      if (action.payload && action.payload.code === EXCEPTION_PASSWORD_RESET) {
        state.isForgotPassword = true;
        state.isPasswordReset = true;
      }
    },
    [signOut.pending]: (state) => {
      state.isPending = true;
    },
    [signOut.fulfilled]: (state) => {
      state.isPending = false;
      state.currentUser = null;
      state.currentSession = null;
      state.message = '';
      state.remember = false;
      sessionStorage.clear();
      localStorage.clear();
    },
    [signOut.rejected]: (state, action) => {
      state.isPending = false;
      state.message = action.payload && action.payload.message;
      state.severity = SEVERITY_ERROR;
    },
    [signUp.pending]: (state) => {
      state.isPending = true;
    },
    [signUp.fulfilled]: (state, action) => {
      state.isPending = false;

      if (action.payload && action.payload.userConfirmed) {
        state.currentUser = action.payload && action.payload.user;

        if (state.currentUser.challengeName === CHALLENGE_NEW_PASSWORD) {
          state.isNewPasswordRequired = true;
        } else if (!state.currentUser.attributes.email_verified) {
          state.isUnverifiedEmail = true;
        } else {
          state.currentSession = state.currentUser.getSignInUserSession();
          state.isNewPasswordRequired = false;

          if (state.remember) {
            localStorage.setItem(STORAGE_GR_AUTH_SESSION, JSON.stringify(state.currentSession));
          } else {
            sessionStorage.setItem(STORAGE_GR_AUTH_SESSION, JSON.stringify(state.currentSession));
          }
        }
      } else {
        state.message = MESSAGE_ADMINISTRATOR_APPROVAL;
        state.severity = SEVERITY_INFO;
        state.isUserRegistration = false;
      }
    },
    [signUp.rejected]: (state, action) => {
      state.isPending = false;
      state.message = action.payload && action.payload.message;
      state.severity = SEVERITY_ERROR;
    },
    [completeNewPassword.pending]: (state) => {
      state.isPending = true;
    },
    [completeNewPassword.fulfilled]: (state, action) => {
      state.isPending = false;
      state.message = MESSAGE_PASSWORD_UPDATED;
      state.severity = SEVERITY_SUCCESS;
      state.currentUser = action.payload;
      state.isNewPasswordRequired = false;
      if (!state.currentUser.attributes || !state.currentUser.attributes.email_verified) {
        state.isUnverifiedEmail = true;
      } else {
        state.currentSession = state.currentUser.getSignInUserSession();

        if (state.remember) {
          localStorage.setItem(STORAGE_GR_AUTH_SESSION, JSON.stringify(state.currentSession));
        } else {
          sessionStorage.setItem(STORAGE_GR_AUTH_SESSION, JSON.stringify(state.currentSession));
        }
      }
    },
    [completeNewPassword.rejected]: (state, action) => {
      state.isPending = false;
      state.message = action.payload && action.payload.message;
      state.severity = SEVERITY_ERROR;
    },
    [forgotPassword.pending]: (state) => {
      state.isPending = true;
    },
    [forgotPassword.fulfilled]: (state) => {
      state.isPending = false;
      state.message = MESSAGE_PASSWORD_RECOVERY_CODE;
      state.severity = SEVERITY_INFO;
      state.isRecoveryCodeSent = true;
    },
    [forgotPassword.rejected]: (state, action) => {
      state.isPending = false;
      state.severity = SEVERITY_ERROR;

      if (action.payload && action.payload.code === EXCEPTION_USER_NOT_FOUND) {
        state.message = MESSAGE_USER_NOT_FOUND;
      } else {
        state.message = action.payload && action.payload.message;
      }
    },
    [forgotPasswordSubmit.pending]: (state) => {
      state.isPending = true;
    },
    [forgotPasswordSubmit.fulfilled]: (state) => {
      state.isPending = false;
      state.message = MESSAGE_PASSWORD_UPDATED;
      state.severity = SEVERITY_SUCCESS;
      state.isForgotPassword = false;
      state.isRecoveryCodeSent = false;
    },
    [forgotPasswordSubmit.rejected]: (state, action) => {
      state.isPending = false;
      state.severity = SEVERITY_ERROR;

      if (action.payload && action.payload.code === EXCEPTION_USER_NOT_FOUND) {
        state.message = MESSAGE_USER_NOT_FOUND;
      } else {
        state.message = action.payload && action.payload.message;
      }
    },
    [verifyUserAttribute.pending]: (state) => {
      state.isPending = true;
    },
    [verifyUserAttribute.fulfilled]: (state) => {
      state.isPending = false;
      state.isVerificationCodeSent = true;
    },
    [verifyUserAttribute.rejected]: (state, action) => {
      state.isPending = false;
      state.message = action.payload && action.payload.message;
      state.severity = SEVERITY_ERROR;
    },
    [verifyUserAttributeSubmit.pending]: (state) => {
      state.isPending = true;
    },
    [verifyUserAttributeSubmit.fulfilled]: (state) => {
      state.isPending = false;
      state.message = MESSAGE_EMAIL_VERIFIED;
      state.severity = SEVERITY_SUCCESS;
      state.isUnverifiedEmail = false;
      state.isVerificationCodeSent = false;
    },
    [verifyUserAttributeSubmit.rejected]: (state, action) => {
      state.isPending = false;
      state.message = action.payload && action.payload.message;
      state.severity = SEVERITY_ERROR;
    },
    [setCurrentUser.fulfilled]: (state, action) => {
      state.currentUser = action.payload;
    },
    [setCurrentUser.rejected]: (state, action) => {
      state.message = action.payload && action.payload.message;
      state.severity = SEVERITY_ERROR;
    },
    [setCurrentSession.fulfilled]: (state, action) => {
      state.currentSession = action.payload && action.payload;

      if (state.remember) {
        localStorage.setItem(STORAGE_GR_AUTH_SESSION,
          (state.currentSession && state.currentSession !== 'No current user')
            ? JSON.stringify(state.currentSession)
            : null);
      } else {
        sessionStorage.setItem(STORAGE_GR_AUTH_SESSION,
          (state.currentSession && state.currentSession !== 'No current user')
            ? JSON.stringify(state.currentSession)
            : null);
      }
    },
    [setCurrentSession.rejected]: (state, action) => {
      state.message = action.payload && action.payload.message;
      state.severity = SEVERITY_ERROR;
    },
  },
});

// Actions

export const {
  setIsForgotPassword,
  setIsUserRegistration,
  setIsPasswordReset,
  setRememberStatus,
  clearMessage,
  setMessage,
} = authSlice.actions;

// Selectors

export const selectIsAuthenticated = (state) => (!!state.auth.currentSession
  && (state.auth.currentSession !== 'No current user'));
export const selectMessage = (state) => (state.auth.message);
export const selectSeverity = (state) => (state.auth.severity);
export const selectRememberStatus = (state) => (state.auth.remember);
export const selectIsNewPasswordRequired = (state) => (state.auth.isNewPasswordRequired);
export const selectIsForgotPassword = (state) => (state.auth.isForgotPassword);
export const selectIsRecoveryCodeSent = (state) => (state.auth.isRecoveryCodeSent);
export const selectIsPasswordReset = (state) => (state.auth.isPasswordReset);
export const selectIsUserRegistration = (state) => (state.auth.isUserRegistration);
export const selectIsUnverifiedEmail = (state) => (state.auth.isUnverifiedEmail);
export const selectIsVerificationCodeSent = (state) => (state.auth.isVerificationCodeSent);
export const selectIsPending = (state) => (state.auth.isPending);
export const selectCurrentUser = (state) => (state.auth.currentUser);
export const selectCurrentSession = (state) => (state.auth.currentSession);
export const selectUsername = (state) => (state.auth.currentSession
  && state.auth.currentSession?.idToken?.payload
  && state.auth.currentSession?.idToken?.payload['cognito:username']);
export const selectUserClientId = (state) => (state.auth.currentUser
  && state.auth.currentUser.attributes['custom:client_id']);

// Reducer

export default authSlice.reducer;
